/**
 * @module support/shared/widgets
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  Provider,
  forwardRef,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/* tslint:disable:no-forward-ref */

/**
 * Provider Expression that allows tmo-checkbox to register as a ControlValueAccessor.
 * This allows it to support [(ngModel)].
 * @docs-private
 */
export const RADIO_CONTROL_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => RadioButtonComponent),
  multi: true,
};

/**
 * A T-mobile customm design radio button component. Supports all of the functionality of an HTML5 radio,
 * and exposes a similar API. A tmo-radio-button can have default selection, disabled,and small text.
 * Note that all additional accessibility attributes are taken care of by the component,
 * so there is no need to provide them yourself.
 */
@Component({
  selector: 'tmo-radio-button',
  templateUrl: './radio-button.component.html',
  styleUrls: ['../../../../../../style-sheets/components/input-radio.component.scss'],
  providers: [RADIO_CONTROL_VALUE_ACCESSOR],
})
export class RadioButtonComponent
  implements OnInit, ControlValueAccessor, OnDestroy, AfterViewInit
{
  /**
   * Name input property to be assigned to each checkbox.
   * IMPORTANT : if it is used inside a <form>, name has to be unique
   */
  @Input() public name?: string;
  /**
   * String input property to get value of the radio button.
   */
  @Input() public values: string;
  /**
   * String input property to set whether the radio button will be selected on load.
   */
  @Input() public selectedValue?: string;
  /**
   * String input property to set whether the radio button will be selected on load.
   */
  @Input() public secondaryText?: string;
  /**
   * Boolean input property to disable the radio button.
   */
  @Input() public disabled?: boolean;
  /**
   * Attached to the aria-label attribute of the host element. In most cases, arial-label will
   * take precedence so this may be omitted.
   */
  /* tslint:disable:no-input-rename */
  @Input('aria-label') public ariaLabel?: string;

  /**
   * Boolean property to add small class to ng-content of radio button.
   */
  @Input() public applySmallClass?: boolean;
  @Input() public errorBorderClass?: boolean;

  /**
   * Boolean property to add small class to subtext of radio button
   */
  @Input() public applySmallSecondaryClass?: boolean;

  /** Event emitted when the dropdown's `onBlur` value changes.
   * It emits the 'Event' object.
   */
  @Output() public onSelect: EventEmitter<Object>;

  /**
   * Temporary input to apply the correct html for tooltips
   * without breaking all existing radio components
   * // TODO: Replace this when radio component is refactored.
   *
   * @type {boolean}
   * @memberof RadioButtonComponent
   */
  @Input() public toolTip: boolean;

  /** Internal property to store current state of input box. */
  private innerValue: {};

  /**
   * id input property to be assigned to each checkbox.
   * IMPORTANT : if it is used inside a <form>, name has to be unique
   */
  @Input() public id?: string;
  /**
   * Whenever the dropdown component needs some value to be instantiated on load of this component
   * values of its properties are initialised in the constructor
   */
  constructor() {
    this.onSelect = new EventEmitter();
    this.innerValue = '';
  }

  /**
   * Called whenever the dropdown component is invoked from parent component
   */
  public ngOnInit(): void {
    if (this.disabled === undefined) {
      this.disabled = false;
    }
    if (this.applySmallClass === undefined) {
      this.applySmallClass = true;
    }
    if (this.applySmallSecondaryClass === undefined) {
      this.applySmallSecondaryClass = false;
    }
    if (this.errorBorderClass === undefined) {
      this.errorBorderClass = false;
    }
  }

  /**
   * Called whenever the radio button is clicked
   * Emits an event with 'event' object as data.
   * @param item
   */
  public onClick(item: string): void {
    this.onSelect.emit(item);
  }

  /**
   * ngModel accessor to get the value of property [based on input type]
   * @docs-private
   */
  get value(): {} {
    return this.innerValue;
  }

  /**
   * ngModel accessor to set the value
   * @param value - {} value: could be number or string
   * @docs - private
   */
  set value(value: {}) {
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.onChangeCallback(value);
    }
  }

  /**
   * Sets the model value. Implemented as part of ControlValueAccessor.
   * @param value Value to be set to the model.
   */
  public writeValue(value: Object): void {
    if (value !== this.innerValue) {
      this.innerValue = value;
      value = this.innerValue;
      this.onChangeCallback(value);
    }
  }

  /**
   * Registers a callback to be triggered when the value has changed.
   * Implemented as part of ControlValueAccessor.
   * @param fn Function to be called on change.
   */
  public registerOnChange(fn: (_: boolean) => {}): void {
    this.onChangeCallback = fn;
  }

  /**
   * Registers a callback to be triggered when the control has been touched.
   * Implemented as part of ControlValueAccessor.
   * @param fn Callback to be triggered when the input is touched.
   */
  public registerOnTouched(fn: () => {}): void {
    this.onTouchedCallback = fn;
  }

  public ngOnDestroy(): void {
    this.onSelect.unsubscribe();
  }
  public ngAfterViewInit(): void {
    setTimeout(() => {
      // Changes for radio button not getting selected on safari
      if (this.values === this.selectedValue) {
        const element = document.getElementById(this.id);
        if (element) {
          element.setAttribute('checked', 'checked');
        }
      }
    }, 0);
  }
  /**
   * Called when the input is blurred. Needed to properly implement ControlValueAccessor.
   * @docs-private
   * @returns void
   */
  private onTouchedCallback(): void {}

  /**
   * Called when the input value is changed. Needed to properly implement ControlValueAccessor.
   * @docs-private
   * @returns void
   */
  private onChangeCallback(_: {}): void {}

  /**
   * Called whenever a radio is focused out
   */
  public onFocusOut(): void {
    this.onTouchedCallback();
  }
}
